'use client';

import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star } from 'lucide-react';
import Image from 'next/image';

const testimonials = [
  {
    quote: "TinyChapters turned my fridge art into bedtime stories. Now my 4-year-old begs to draw more just so we can make new books together!",
    author: 'Amanda Chen',
    location: 'Seattle, WA',
    avatar: '/marketing/avatar-amanda-chen.png',
    rating: 5,
  },
  {
    quote: "I was drowning in artwork from two kids. Now everything is preserved digitally AND turned into stories they actually want to read.",
    author: 'Rachel Thompson',
    location: 'Austin, TX',
    avatar: '/marketing/avatar-rachel-thompson.png',
    rating: 5,
  },
  {
    quote: "My mom cried when she got the printed book of my son's drawings. It's become the most treasured gift we've ever given her.",
    author: 'Maria Rodriguez',
    location: 'Miami, FL',
    avatar: '/marketing/avatar-maria-rodriguez.png',
    rating: 5,
  },
  {
    quote: "Bedtime used to be a struggle. Now my daughter can't wait to hear stories about her own drawings. Game changer!",
    author: 'Emily Watson',
    location: 'Denver, CO',
    avatar: '/marketing/avatar-emily-watson.png',
    rating: 5,
  },
  {
    quote: "Finally, a way to save all those precious drawings without filling every drawer in the house. The stories are magical.",
    author: 'Priya Patel',
    location: 'Chicago, IL',
    avatar: '/marketing/avatar-priya-patel.png',
    rating: 5,
  },
  {
    quote: "My son has autism and struggles with reading. Seeing his own art in stories has sparked a love for books I never thought possible.",
    author: 'Jennifer O\'Brien',
    location: 'Portland, OR',
    avatar: '/marketing/avatar-jennifer-obrien.png',
    rating: 5,
  },
];

/**
 * Testimonials section - rolling carousel
 *
 * Features:
 * - Continuous scrolling carousel
 * - Multiple visible testimonials
 * - Auto-scroll with pause on hover
 * - Real mother testimonials
 */
export function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, []);

  // Auto-scroll
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(goToNext, 4000);
    return () => clearInterval(interval);
  }, [isPaused, goToNext]);

  // Get visible testimonials (current + next 2)
  const getVisibleTestimonials = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % testimonials.length;
      visible.push({ ...testimonials[index], index });
    }
    return visible;
  };

  return (
    <section className="py-16 md:py-24 bg-cream-100">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-6xl">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-semibold text-charcoal-600 mb-4">
            Loved by Parents
          </h2>
          <p className="text-taupe-500 text-base md:text-lg max-w-2xl mx-auto">
            Join thousands of families preserving childhood memories
          </p>
        </motion.div>

        {/* Rolling Carousel */}
        <div
          className="relative overflow-hidden"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="flex gap-6">
            <AnimatePresence mode="popLayout" initial={false}>
              {getVisibleTestimonials().map((testimonial, i) => (
                <motion.div
                  key={`${testimonial.index}-${currentIndex}`}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{
                    opacity: i === 0 ? 1 : i === 1 ? 0.85 : 0.6,
                    x: 0,
                    scale: i === 0 ? 1 : i === 1 ? 0.95 : 0.9,
                  }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.5 }}
                  className={`flex-shrink-0 w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] ${
                    i === 0 ? '' : 'hidden md:block'
                  } ${i === 2 ? 'lg:block md:hidden' : ''}`}
                >
                  <div className="bg-white rounded-2xl shadow-md p-6 md:p-8 h-full flex flex-col">
                    {/* Star Rating */}
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, j) => (
                        <Star
                          key={j}
                          className="w-4 h-4 fill-amber-400 text-amber-400"
                        />
                      ))}
                    </div>

                    {/* Quote */}
                    <blockquote className="text-charcoal-600 leading-relaxed mb-6 flex-grow">
                      &ldquo;{testimonial.quote}&rdquo;
                    </blockquote>

                    {/* Author */}
                    <div className="flex items-center gap-3">
                      {/* Avatar */}
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 relative">
                        <Image
                          src={testimonial.avatar}
                          alt={testimonial.author}
                          fill
                          className="object-cover"
                        />
                      </div>

                      {/* Name and Location */}
                      <div>
                        <div className="font-medium text-charcoal-600 text-sm">
                          {testimonial.author}
                        </div>
                        <div className="text-xs text-taupe-400">
                          {testimonial.location}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-terracotta-500 w-6'
                    : 'bg-taupe-300 hover:bg-taupe-400'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
